import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Button } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ColResizeDirective } from './col-resize.directive';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormsModule } from '@angular/forms';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { FaTasksStateService } from './fa-tasks-state.service';
import {
  FaTask,
  FaTaskDueDateFilter,
  FaTaskPriority,
  FaTaskSortBy,
  FaTaskStatus,
  FaTasksState,
} from './fa-tasks.model';
import { Opportunity } from './client-actions.model';
import { FaTaskAddDialogComponent } from './fa-task-add-dialog/fa-task-add-dialog.component';
import { FaTaskDetailsDialogComponent, FaTaskDialogResult } from './fa-task-details-dialog/fa-task-details-dialog.component';

const EMPTY_STATE: FaTasksState = { tasks: [] };

const ALL_TASK_STATUSES: FaTaskStatus[] = [
  'To Do',
  'In Progress',
  'Done',
  'Blocked',
  'Withdrawn',
];

const ALL_PRIORITIES: FaTaskPriority[] = ['P1', 'P2', 'P3', 'P4'];

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Statuses', value: '' },
  ...ALL_TASK_STATUSES.map((s) => ({ label: s, value: s })),
];

const PRIORITY_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Priorities', value: '' },
  ...ALL_PRIORITIES.map((p) => ({ label: p, value: p })),
];

const SORT_OPTIONS: { label: string; value: FaTaskSortBy }[] = [
  { label: 'Sort by Due Date', value: 'dueDate' },
  { label: 'Sort by Priority', value: 'priority' },
  { label: 'Sort by Created', value: 'createdDate' },
  { label: 'Sort by Updated', value: 'updatedDate' },
];

const DUE_DATE_OPTIONS: { label: string; value: FaTaskDueDateFilter }[] = [
  { label: 'All Due Dates', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'Next 7 Days', value: 'next7' },
  { label: 'Overdue', value: 'overdue' },
];

const PRIORITY_ORDER: Record<FaTaskPriority, number> = {
  P1: 0,
  P2: 1,
  P3: 2,
  P4: 3,
};

@Component({
  selector: 'bai-fa-tasks',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, ColResizeDirective, FormsModule, SelectModule, TagModule],
  providers: [DialogService],
  templateUrl: './fa-tasks.component.html',
  styleUrl: './fa-tasks.component.scss',
})
export class FaTasksComponent {
  private _stateService = inject(FaTasksStateService);
  private _clientSession = inject(ClientSessionService);
  private _dialogService = inject(DialogService);
  private _copilotComposer = inject(CopilotContextComposerService);
  private _dialogRef: DynamicDialogRef | null = null;

  /** Opportunities from the parent ClientActionsComponent for linking tasks. */
  readonly opportunities = input<Opportunity[]>([]);

  readonly sortOptions = SORT_OPTIONS;
  readonly dueDateOptions = DUE_DATE_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly priorityOptions = PRIORITY_OPTIONS;

  private readonly _sessionId = computed(
    () => this._clientSession.selectedSession()?.uiSessionId ?? ''
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) this._stateService.ensureState(id);
    });
  }

  readonly state = computed(() => {
    const id = this._sessionId();
    if (!id) return EMPTY_STATE;
    return this._stateService.storeSnapshot()[id] ?? EMPTY_STATE;
  });

  /** Status dropdown filter — empty string means all statuses */
  readonly taskFilterValue = signal<string>('');

  /** Priority dropdown filter — empty string means all priorities */
  readonly priorityFilterValue = signal<string>('');

  /** Label dropdown filter — empty string means all labels */
  readonly labelFilterValue = signal<string>('');

  /** Due date window filter */
  readonly dueDateFilter = signal<FaTaskDueDateFilter>('all');

  /** Assignee filter — empty string means all assignees */
  readonly assigneeFilter = signal<string>('');

  /** Sort field */
  readonly sortBy = signal<FaTaskSortBy>('dueDate');

  /** All unique labels across all tasks (for filter chips) */
  readonly allLabels = computed(() => {
    const labels = new Set<string>();
    this.state().tasks.forEach((t) => t.labels.forEach((l) => labels.add(l)));
    return Array.from(labels).sort();
  });

  /** All unique assignees across all tasks */
  readonly allAssignees = computed(() => {
    const assignees = new Set<string>();
    this.state().tasks.forEach((t) => {
      if (t.assignee) assignees.add(t.assignee);
    });
    return Array.from(assignees).sort();
  });

  /** Assignee dropdown options including "All Assignees" placeholder */
  readonly assigneeOptions = computed(() => [
    { label: 'All Assignees', value: '' },
    ...this.allAssignees().map((a) => ({ label: a, value: a })),
  ]);

  /** Label dropdown options including "All Labels" placeholder */
  readonly labelOptions = computed(() => [
    { label: 'All Labels', value: '' },
    ...this.allLabels().map((l) => ({ label: l, value: l })),
  ]);

  readonly filteredTasks = computed(() => {
    const today = new Date().toISOString().slice(0, 10);
    const next7 = new Date(Date.now() + 7 * 86400000)
      .toISOString()
      .slice(0, 10);

    let tasks = this.state().tasks;

    // Status filter — empty means all
    const statusFilter = this.taskFilterValue();
    if (statusFilter) {
      tasks = tasks.filter((t) => t.status === statusFilter);
    }

    // Priority filter — empty means all
    const priorityFilter = this.priorityFilterValue();
    if (priorityFilter) {
      tasks = tasks.filter((t) => t.priority === priorityFilter);
    }

    // Label filter — empty means all
    const labelFilter = this.labelFilterValue();
    if (labelFilter) {
      tasks = tasks.filter((t) => t.labels.includes(labelFilter));
    }

    // Assignee filter
    const assignee = this.assigneeFilter();
    if (assignee) {
      tasks = tasks.filter((t) => t.assignee === assignee);
    }

    // Due date filter
    const dueDateFilter = this.dueDateFilter();
    if (dueDateFilter === 'today') {
      tasks = tasks.filter((t) => t.dueDate === today);
    } else if (dueDateFilter === 'next7') {
      tasks = tasks.filter((t) => t.dueDate >= today && t.dueDate <= next7);
    } else if (dueDateFilter === 'overdue') {
      tasks = tasks.filter(
        (t) =>
          t.dueDate &&
          t.dueDate < today &&
          t.status !== 'Done' &&
          t.status !== 'Withdrawn'
      );
    }

    // Sort
    const sortBy = this.sortBy();
    tasks = [...tasks].sort((a, b) => {
      if (sortBy === 'priority') {
        return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      }
      const av = a[sortBy] ?? '';
      const bv = b[sortBy] ?? '';
      return av < bv ? -1 : av > bv ? 1 : 0;
    });

    return tasks;
  });

  subtaskProgress(task: FaTask): string {
    const total = task.subtasks.length;
    if (total === 0) return '';
    const done = task.subtasks.filter((s) => s.done).length;
    return `${done}/${total}`;
  }

  isOverdue(task: FaTask): boolean {
    if (!task.dueDate) return false;
    const today = new Date().toISOString().slice(0, 10);
    return (
      task.dueDate < today &&
      task.status !== 'Done' &&
      task.status !== 'Withdrawn'
    );
  }

  opportunityLabel(
    opportunityId: string | null,
    opportunities: Opportunity[]
  ): string {
    if (!opportunityId) return '—';
    const opp = opportunities.find((o) => o.id === opportunityId);
    return opp ? opp.opportunityDescription : '—';
  }

  prioritySeverity(
    priority: FaTaskPriority
  ): 'danger' | 'warn' | 'info' | 'secondary' {
    switch (priority) {
      case 'P1':
        return 'danger';
      case 'P2':
        return 'warn';
      case 'P3':
        return 'info';
      default:
        return 'secondary';
    }
  }

  statusSeverity(
    status: FaTaskStatus
  ): 'success' | 'info' | 'secondary' | 'warn' | 'danger' {
    switch (status) {
      case 'Done':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'To Do':
        return 'secondary';
      case 'Blocked':
        return 'warn';
      case 'Withdrawn':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  openAddTask(opportunities: Opportunity[]): void {
    this._dialogRef = this._dialogService.open(FaTaskAddDialogComponent, {
      header: 'Add FA Task',
      width: '560px',
      data: { opportunities },
    });
    this._dialogRef.onClose.subscribe((result) => {
      if (result) {
        const sessionId = this._sessionId();
        if (sessionId) this._stateService.addTask(sessionId, result);
      }
    });
  }

  openTaskDetails(task: FaTask, opportunities: Opportunity[]): void {
    this._dialogRef = this._dialogService.open(FaTaskDetailsDialogComponent, {
      header: 'FA Task Details',
      width: '560px',
      data: { task, opportunities },
    });
    this._dialogRef.onClose.subscribe((result: FaTaskDialogResult | null) => {
      if (result) {
        const sessionId = this._sessionId();
        if (sessionId) {
          this._stateService.updateTask(sessionId, task.id, {
            ...result.patch,
            subtasks: result.subtasks,
          });
        }
      }
    });
  }

  addTaskToCopilot(task: FaTask): void {
    const preview = this.truncate(task.taskName, 20);
    this._copilotComposer.addContext({
      sourceType: 'actions-fa-task',
      sourceId: task.id,
      label: `FA Task: ${preview}`,
      fullText:
        `[Actions | FA Task | ${task.id}] ` +
        `Task Name: ${task.taskName}; ` +
        `Description: ${task.description || '—'}; ` +
        `Opportunity: ${this.opportunityLabel(task.opportunityId, this.opportunities())}; ` +
        `Assignee: ${task.assignee}; Priority: ${task.priority}; Status: ${task.status}; ` +
        `Labels: ${task.labels.length > 0 ? task.labels.join(', ') : '—'}; ` +
        `Subtasks: ${this.subtaskProgress(task) || '—'}; ` +
        `Created: ${task.createdDate}; Due Date: ${task.dueDate || '—'}; ` +
        `Planned: ${task.plannedDate || '—'}; Updated: ${task.updatedDate}`,
    });
  }

  truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
